import { useRef, useEffect } from 'react';
import { useTheme } from 'next-themes';
import { createChart, ColorType, Time, MouseEventParams, BarData, LineData, UTCTimestamp, CrosshairMode } from 'lightweight-charts';
import { Address } from '@shared/types/frontend';
import { ResolutionData, resolutionData } from '@/lib/tradingview/chartData';
import useIsPWA from '@/hooks/utilities/useIsPWA';
import { useSession } from '@/contexts/LitSessionProvider';
import { formatChartDate, usePortfolioChartData } from '@/hooks/charts/usePortfolioChartData';
import { IUserERC20Balance } from '@/hooks/portfolio/useERC20Balance';
import Loading from '../shared/Loading';
export interface CurrentPrice {
  value: number;
  time: UTCTimestamp;
  before?: number;
}
interface PortfolioChartProps {
  onValueChange?: (_now: number, _before: number, _time: UTCTimestamp) => void;
  setCurrentPrice: (_: CurrentPrice) => void;
  currentResolution: ResolutionData;
  setCurrentResolution: (_: ResolutionData) => void;
  assetData?: IUserERC20Balance;
}
const PortfolioChart: React.FC<PortfolioChartProps> = ({
  onValueChange,
  setCurrentPrice,
  currentResolution,
  setCurrentResolution,
  assetData
}) => {
  const {
    PKP
  } = useSession();
  const [wallet, solanaWallet] = [PKP?.additionalEthAddress, PKP?.solanaAddress];
  const {
    data: chartBarData,
    isLoading: isLoadingChartBarData,
    refetch: refetchPortfolio
  } = usePortfolioChartData({
    ethWallet: wallet as Address,
    solanaWallet: solanaWallet as string,
    currentResolution
  });
  const isPWA = useIsPWA();
  const height = isPWA ? 265 : 280;
  const chartContainerRef: any = useRef();
  const {
    theme
  } = useTheme();
  useEffect(() => chartBarData ? setCurrentPrice(chartBarData[chartBarData.length - 1]) : undefined, [chartBarData]);
  useEffect(() => {
    const handleResize = () => {
      if (chartContainerRef.current && chartContainerRef.current.clientWidth) {
        chart.applyOptions({
          width: chartContainerRef?.current?.clientWidth
        });
      }
    };
    const chart = createChart(chartContainerRef?.current, {
      layout: {
        background: {
          type: ColorType.Solid,
          color: theme === 'dark' ? '#09001A' : '#FAF5FF'
        },
        textColor: '#666666'
      },
      grid: {
        vertLines: {
          visible: false
        },
        horzLines: {
          visible: false
        }
      },
      rightPriceScale: {
        borderVisible: false,
        visible: false
      },
      timeScale: {
        borderVisible: false,
        timeVisible: false,
        visible: false
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        horzLine: {
          visible: false,
          labelVisible: false
        },
        vertLine: {
          visible: true,
          labelVisible: false
        }
      },
      width: chartContainerRef?.current?.clientWidth + 40,
      height: height,
      handleScale: false,
      handleScroll: false
    });
    const firstValue = chartBarData?.[0]?.value ? chartBarData[0].value : 1;
    const lastValue = chartBarData?.[chartBarData.length - 1]?.value ? chartBarData[chartBarData.length - 1].value : 0;
    const isPositive = lastValue > firstValue;
    const areaSeries = chart.addAreaSeries({
      topColor: theme === 'dark' ? isPositive ? 'rgb(30, 79, 44,0%)' : 'rgb(35, 4, 2,0)' : 'rgba(9, 0, 26, 0)',
      bottomColor: theme === 'dark' ? 'rgba(9, 0, 26, 90%)' : 'rgba(9, 0, 26, 0)',
      lineColor: isPositive ? '#5de2a4' : '#F87171',
      baseLineColor: '#FFA3C8',
      priceLineColor: '#FFA3C8',
      lineType: 2,
      lineWidth: 1,
      crosshairMarkerVisible: true,
      priceLineVisible: false
    });
    areaSeries.priceScale().applyOptions({
      scaleMargins: {
        top: 0.3,
        bottom: 0.25
      }
    });
    if (chartBarData) {
      areaSeries.setData(chartBarData);
    }
    const container = document.getElementById('chartContainer');
    const toolTipWidth = 80;
    const allToolTips = document.querySelectorAll('.chartToolTipDesktop');
    if (allToolTips && allToolTips.length) {
      allToolTips.forEach(tooltip => {
        tooltip.remove();
      });
    }
    const toolTip = document.createElement('div');
    toolTip.className = `chartToolTipDesktop`;
    container.appendChild(toolTip);
    window.addEventListener('resize', handleResize);
    chart.subscribeClick((param: MouseEventParams<Time>) => {
      if (param.point === undefined || !param.time || param.point.x < 0 || param.point.x > chartContainerRef?.current.clientWidth || param.point.y < 0 || param.point.y > chartContainerRef?.current.clientHeight) {
        return;
      } else {
        const dateVal = new Date(parseInt(param.time as string) * 1000);
        const currentData = param.seriesData.get(areaSeries) as LineData<Time> | BarData<Time>;
        const price = (currentData as LineData).value || (currentData as BarData).close;
        onValueChange(price, chartBarData[0].value, dateVal.getTime() / 1000 as UTCTimestamp);
      }
    });
    chart.subscribeCrosshairMove((param: MouseEventParams<Time>) => {
      if (param.point === undefined || !param.time || param.point.x < 0 || param.point.x > chartContainerRef?.current.clientWidth || param.point.y < 0 || param.point.y > chartContainerRef?.current.clientHeight) {
        toolTip.style.display = 'none';
      } else {
        const dateVal: Date = new Date(parseInt(param.time as string) * 1000);
        const currentData: any = param.seriesData.get(areaSeries);
        const price = currentData.value !== undefined ? currentData.value : currentData.close;
        toolTip.style.display = 'block';
        toolTip.style.backgroundColor = isPositive ? '#34d399' : '#f87171';
        toolTip.style.height = '22px';
        toolTip.style.width = 'fit-content';
        toolTip.style.paddingLeft = '10px';
        toolTip.style.paddingRight = '10px';
        toolTip.style.borderRadius = '4px';
        toolTip.style.fontSize = '14px';
        toolTip.style.color = '#09001a';
        toolTip.innerHTML = `
        <div>
       ${formatChartDate(dateVal, currentResolution.periodLabel)}
        </div>`;
        const coordinate = areaSeries.priceToCoordinate(price);
        let shiftedCoordinate = param.point.x - 50;
        if (coordinate === null) {
          return;
        }
        shiftedCoordinate = Math.max(0, Math.min(container.clientWidth - toolTipWidth, shiftedCoordinate));
        toolTip.style.left = shiftedCoordinate + 'px';
        onValueChange(price, chartBarData[0].value, dateVal.getTime() / 1000 as UTCTimestamp);
      }
    });
    chartContainerRef.current.addEventListener('mouseleave', () => {
      toolTip.style.display = 'none';
      setCurrentPrice({
        value: assetData?.totalUsdValue,
        before: assetData?.totalUsdValue24h,
        time: Date.now() as UTCTimestamp
      });
    });
    chart.timeScale().fitContent();
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
    chartContainerRef.current.addEventListener('mouseleave', () => {
      toolTip.style.display = 'none';
    });
    return () => {
      window.removeEventListener('resize', handleResize);
      chartContainerRef.current.removeEventListener('mouseleave', () => {
        toolTip.style.display = 'none';
      });
      chart.remove();
    };
  }, [chartBarData]);
  if (!currentResolution) return;
  return <div className='w-full chart-box items-center justify-center bg-genius-indigo min-h-[270px] relative' data-sentry-component="PortfolioChart" data-sentry-source-file="PortfolioChart.tsx">
      <div className='z-20 absolute w-full h-full flex justify-center items-center pb-[80px] pointer-events-none'>
        <img src='/static/geniusImages/trade-genius-com-logo.png' alt='genius-logo' width={100} height={100} className='w-[270px] h-[40px] pointer-events-none' />
      </div>
      <div className='w-full relative -translate-x-4' ref={chartContainerRef} id='chartContainer' />
      {isLoadingChartBarData && <div className='absolute inset-x-0 top-1/2 z-50'>
          <Loading />
        </div>}
      {/* {isLoadingChartBarData && (
        <div className='absolute inset-x-0 top-[35%] z-50'>
          <Skeleton className='w-[90%] h-64 mx-auto' />
        </div>
       )} */}
      {/* time frames */}
      <div className='flex max-w-[300px] items-center gap-1 justify-center p-1 mt-2 mb-8 mx-auto chartFrames'>
        {resolutionData.map(item => <div key={`resolution-${item.periodLabel}`} onClick={() => {
        refetchPortfolio();
        setCurrentResolution(item);
      }}>
            <div className={`text-genius-cream cursor-pointer rounded-lg py-2 px-4 text-xs ${currentResolution.periodLabel === item.periodLabel ? 'bg-genius-blue' : 'text-genius-dark bg-genius-indigo font-normal'}`}>
              {item.periodLabel}
            </div>
          </div>)}
      </div>
    </div>;
};
export default PortfolioChart;